// CE Bundle Export — generate evidence bundles and export report artifacts.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';

const BUNDLE_TYPES = [
  { value: 'period',   label: 'Period',   desc: 'All evidence for a time window' },
  { value: 'control',  label: 'Control',  desc: 'Evidence for a specific control' },
  { value: 'incident', label: 'Incident', desc: 'Incident case + supporting evidence' },
  { value: 'ad_hoc',  label: 'Ad Hoc',   desc: 'Manually selected objects' },
];

const ARTIFACT_TYPES = [
  { value: 'json_export', label: 'JSON Export (stub)' },
  { value: 'archive',     label: 'Archive (stub)' },
];

const CSV_EXPORT_TYPES = [
  { value: 'observed-events', label: 'Observed Events' },
  { value: 'derived-events',  label: 'Derived Events' },
];

function parseFilename(contentDisposition, fallback) {
  const m = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/);
  return m ? m[1] : fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// D3: SEALED_EVIDENCE and PARTIAL_REVIEW are the only evidentiary classes.
// MANUAL_ASSEMBLY has no generation run behind it, and null means pre-D1 or
// unknown provenance — neither may be presented as evidence. Defaulting an
// unknown to "evidentiary" is the silent-affirmation failure this work exists
// to close, so the check is an explicit allow-list.
export const isEvidentiaryClass = (c) =>
  c === 'SEALED_EVIDENCE' || c === 'PARTIAL_REVIEW';

export function EvidenceClassBadge({ evidenceClass }) {
  if (!evidenceClass) return null;
  const styles = {
    SEALED_EVIDENCE: 'bg-green-900/40 text-green-300 border-green-800',
    PARTIAL_REVIEW: 'bg-amber-900/40 text-amber-300 border-amber-800',
    MANUAL_ASSEMBLY: 'bg-gray-700/40 text-gray-300 border-gray-600',
  }[evidenceClass] || 'bg-gray-700/40 text-gray-300 border-gray-600';
  const label = {
    SEALED_EVIDENCE: 'Complete evidence packet',
    PARTIAL_REVIEW: 'Bounded review set — not a complete packet',
    MANUAL_ASSEMBLY: 'Manually assembled — not an evidence packet',
  }[evidenceClass] || evidenceClass;
  return (
    <span
      data-testid="evidence-class-badge"
      className={`inline-block px-2 py-0.5 text-[11px] rounded border ${styles}`}
    >
      {label}
    </span>
  );
}

function EvidenceExportPanel({ bundleId, evidenceClass }) {
  const [downloading, setDownloading] = useState(null);
  const [err, setErr] = useState(null);
  const [truncatedWarning, setTruncatedWarning] = useState(null);

  const handleDownloadPdf = async () => {
    setDownloading('pdf');
    setErr(null);
    setTruncatedWarning(null);
    try {
      const response = await api.get(`/ce/bundles/${bundleId}/export/pdf`, { responseType: 'blob' });
      const filename = parseFilename(response.headers['content-disposition'], `evidence-packet-${bundleId}.pdf`);
      triggerBlobDownload(response.data, filename);
    } catch (e) {
      setErr(errorDetail(e, 'PDF export failed'));
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadCsv = async (eventType, label) => {
    setDownloading(eventType);
    setErr(null);
    setTruncatedWarning(null);
    try {
      const response = await api.get(`/ce/bundles/${bundleId}/export/csv/${eventType}`, { responseType: 'blob' });
      const filename = parseFilename(response.headers['content-disposition'], `${eventType}-${bundleId}.csv`);
      triggerBlobDownload(response.data, filename);
      if (response.headers['x-evidence-truncated'] === 'true') {
        setTruncatedWarning(
          `${label}: this export hit the row ceiling — some records were not included. Contact support if you need the full dataset.`
        );
      }
    } catch (e) {
      setErr(errorDetail(e, 'CSV export failed'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 mt-6">
      <h2 className="text-sm font-semibold text-white mb-1">Downloads</h2>
      <div className="mb-2"><EvidenceClassBadge evidenceClass={evidenceClass} /></div>
      <p className="text-xs text-gray-500 mb-3">
        {isEvidentiaryClass(evidenceClass)
          ? (evidenceClass === 'PARTIAL_REVIEW'
              /* D3 level 3: the previous wording — "rendered directly from this
                 bundle's sealed evidence" — was a completeness claim in plain
                 English, rendered regardless of whether the bundle was bounded. */
              ? "Downloadable exports of this bundle's sealed evidence. This is a bounded review set — one or more categories were capped and it is not a complete evidence package for the period."
              : "Downloadable exports rendered directly from this bundle's sealed evidence.")
          : 'This record is not an evidence packet. Files are provided for reference only.'}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleDownloadPdf}
          disabled={downloading !== null}
          className="px-4 py-2 text-sm rounded-lg bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-blue-100 font-medium transition-colors"
        >
          {downloading === 'pdf' ? 'Rendering…' : 'Download PDF Evidence Packet'}
        </button>
        {CSV_EXPORT_TYPES.map(t => (
          <button
            key={t.value}
            onClick={() => handleDownloadCsv(t.value, t.label)}
            disabled={downloading !== null}
            className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 transition-colors"
          >
            {downloading === t.value ? 'Downloading…' : `${t.label} CSV`}
          </button>
        ))}
      </div>
      {err && <p className="text-xs text-red-400 mt-3">{err}</p>}
      {truncatedWarning && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-300 mt-3">
          {truncatedWarning}
        </div>
      )}
    </div>
  );
}

function BundleManifest({ manifest }) {
  if (!manifest) return null;
  const objectCount = manifest.included_objects?.length ?? 0;

  const handleDownloadJson = () => {
    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bundle-manifest-${manifest.bundle_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-gray-750 rounded-lg border border-gray-700 p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Technical Manifest</h3>
        <button
          onClick={handleDownloadJson}
          className="px-3 py-1.5 text-xs rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
        >
          Download JSON
        </button>
      </div>

      {objectCount === 0 && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-300 mb-3">
          This bundle contains 0 evidence objects — it was likely created with the manual
          Advanced form below rather than the Evidence Packet Generator. It is not an
          audit-ready packet.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div>
          <span className="text-gray-500">Packet ID</span>
          <p className="text-gray-200 font-mono mt-0.5 break-all">{manifest.bundle_id}</p>
        </div>
        <div>
          <span className="text-gray-500">Type</span>
          <p className="text-gray-200 mt-0.5">{manifest.bundle_type}</p>
        </div>
        <div>
          <span className="text-gray-500">Evidence class</span>
          <p className="mt-0.5"><EvidenceClassBadge evidenceClass={manifest.evidence_class} /></p>
        </div>
        <div>
          <span className="text-gray-500">Period</span>
          <p className="text-gray-200 mt-0.5">
            {manifest.period_start ? new Date(manifest.period_start).toLocaleString() : '—'} →{' '}
            {manifest.period_end ? new Date(manifest.period_end).toLocaleString() : '—'}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Integrity hash</span>
          <p className="text-gray-400 font-mono mt-0.5 text-xs break-all">
            {manifest.integrity_hash ? manifest.integrity_hash.slice(0, 32) + '...' : '—'}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Objects included</span>
          <p className={`mt-0.5 font-semibold ${objectCount === 0 ? 'text-amber-400' : 'text-gray-200'}`}>
            {objectCount}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Status</span>
          <p className="text-gray-200 mt-0.5">{manifest.status}</p>
        </div>
      </div>

      {objectCount > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Included objects</p>
          <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {manifest.included_objects.map((obj, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-gray-600 font-mono w-5 shrink-0">{i + 1}</span>
                <span className="px-1.5 py-0.5 rounded border border-gray-600 text-gray-400 font-mono">
                  {obj.object_type}
                </span>
                <span className="text-gray-300 font-mono truncate">{obj.object_id}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ArtifactPanel({ bundleId, onDone }) {
  const [artifactType, setArtifactType] = useState('json_export');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const handleExport = async () => {
    setGenerating(true);
    setResult(null);
    setErr(null);
    try {
      const { data } = await api.post(`/ce/bundles/${bundleId}/artifacts`, {
        artifact_type: artifactType,
        generated_by: 'ui',
      });
      setResult(data);
      onDone?.();
    } catch (e) {
      setErr(errorDetail(e, 'Download failed'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 mt-6">
      <h2 className="text-sm font-semibold text-white mb-3">Export Artifact</h2>
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Artifact type</label>
          <select
            value={artifactType}
            onChange={e => setArtifactType(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {ARTIFACT_TYPES.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleExport}
          disabled={generating}
          className="px-4 py-2 text-sm rounded-lg bg-purple-800 hover:bg-purple-700 disabled:opacity-50 text-purple-100 font-medium transition-colors"
        >
          {generating ? 'Exporting...' : 'Export'}
        </button>
      </div>
      {err && <p className="text-xs text-red-400 mb-2">{err}</p>}
      {result && (
        <div className="space-y-2">
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-300">
            Artifact rendering is not enabled in this environment — this record is a placeholder,
            not a downloadable file.
          </div>
          <div className="bg-gray-700/40 rounded p-3 text-xs font-mono text-gray-300 space-y-1">
            <p>Artifact ID: {result.id}</p>
            <p>Type: {result.artifact_type}</p>
            <p>Storage ref: {result.storage_ref}</p>
            <p>Checksum: {result.checksum}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CEBundleExport() {
  const [searchParams] = useSearchParams();
  const initSite = searchParams.get('site') || '';

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(initSite);
  const [bundle, setBundle] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [err, setErr] = useState(null);

  // Browse bundles for the selected site
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [selectedBundleId, setSelectedBundleId] = useState('');
  const [manualBundleId, setManualBundleId] = useState('');
  const [loadingManifest, setLoadingManifest] = useState(false);

  // Advanced / manual bundle form — collapsed by default
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bundleType, setBundleType] = useState('period');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get('/ce/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const loadBundles = useCallback(async () => {
    if (!siteId) { setBundles([]); return; }
    setLoadingBundles(true);
    setErr(null);
    try {
      const { data } = await api.get(`/ce/sites/${siteId}/bundles`);
      setBundles(data.bundles || []);
    } catch (e) {
      setErr(errorDetail(e, 'Failed to load bundles'));
    } finally {
      setLoadingBundles(false);
    }
  }, [siteId]);

  useEffect(() => { loadBundles(); }, [loadBundles]);

  const handleLoadManifestFor = async (bundleId) => {
    setSelectedBundleId(bundleId);
    setLoadingManifest(true);
    setManifest(null);
    setBundle(null);
    setErr(null);
    try {
      const { data } = await api.get(`/ce/bundles/${bundleId}/manifest`);
      setManifest(data);
      setBundle({ id: bundleId });
    } catch (e) {
      setErr(errorDetail(e, 'Failed to load manifest'));
    } finally {
      setLoadingManifest(false);
    }
  };

  const handleLoadManualId = () => {
    if (manualBundleId) handleLoadManifestFor(manualBundleId);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!siteId || !periodStart || !periodEnd) { setErr('Site, start, and end are required.'); return; }
    setGenerating(true);
    setErr(null);
    setBundle(null);
    setManifest(null);
    try {
      const { data } = await api.post('/ce/bundles', {
        site_id: siteId,
        bundle_type: bundleType,
        period_start: new Date(periodStart).toISOString(),
        period_end: new Date(periodEnd).toISOString(),
        included_object_refs: [],
      });
      setBundle(data);
      const { data: mf } = await api.get(`/ce/bundles/${data.id}/manifest`);
      setManifest(mf);
      loadBundles();
    } catch (e) {
      setErr(errorDetail(e, 'Bundle generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  const siteName = sites.find(s => s.id === siteId)?.site_name || '';

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-1">My Packets</h1>
        <p className="text-sm text-gray-400 mb-6">
          View and export previously generated compliance evidence bundles. To create a
          complete, audit-ready packet, use the{' '}
          <a href="/ce/evidence-packet" className="text-blue-400 hover:text-blue-300 underline">
            Evidence Packet Generator
          </a>.
        </p>

        {/* Browse bundles by site */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 mb-6">
          <h2 className="text-sm font-semibold text-white mb-3">Your packets</h2>
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Site</label>
            <select
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select site</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.site_name}</option>
              ))}
            </select>
          </div>

          {loadingBundles && <p className="text-xs text-gray-500">Loading packets…</p>}

          {!loadingBundles && siteId && bundles.length === 0 && (
            <p className="text-xs text-gray-500">
              No bundles found for {siteName}. Generate one from the{' '}
              <a href={`/ce/evidence-packet?site=${siteId}`} className="text-blue-400 hover:text-blue-300 underline">
                Evidence Packet Generator
              </a>.
            </p>
          )}

          {bundles.length > 0 && (
            <div className="divide-y divide-gray-700/60">
              {bundles.map(b => (
                <button
                  key={b.id}
                  onClick={() => handleLoadManifestFor(b.id)}
                  className={`w-full text-left py-2.5 px-2 -mx-2 rounded transition-colors ${
                    selectedBundleId === b.id ? 'bg-gray-700/60' : 'hover:bg-gray-750'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-200">{b.bundle_type}</span>
                        <span className="text-xs font-mono text-gray-500 truncate">{b.id}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {b.period_start ? new Date(b.period_start).toLocaleString() : '—'} →{' '}
                        {b.period_end ? new Date(b.period_end).toLocaleString() : '—'}
                      </p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full border font-mono ${
                      b.object_count === 0
                        ? 'bg-amber-900/50 text-amber-300 border-amber-700'
                        : 'bg-green-900/50 text-green-300 border-green-700'
                    }`}>
                      {b.object_count} objects
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3 items-end mt-4 pt-4 border-t border-gray-700/60">
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Or open a packet by ID</label>
              <input
                type="text"
                value={manualBundleId}
                onChange={e => setManualBundleId(e.target.value)}
                placeholder="uuid..."
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleLoadManualId}
              disabled={loadingManifest || !manualBundleId}
              className="px-4 py-2 text-sm rounded-lg bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-gray-200 transition-colors"
            >
              {loadingManifest ? 'Loading...' : 'Load'}
            </button>
          </div>

          {err && <p className="text-xs text-red-400 mt-3">{err}</p>}
        </div>

        {/* Manifest */}
        {manifest && <BundleManifest manifest={manifest} />}

        {/* Real, downloadable evidence exports — only when we have a bundle */}
        {bundle?.id && (
          <EvidenceExportPanel evidenceClass={manifest?.evidence_class} bundleId={bundle.id} />
        )}

        {/* Legacy stub artifact export — only when we have a bundle */}
        {bundle?.id && (
          <ArtifactPanel bundleId={bundle.id} />
        )}

        {/* Advanced: manual bundle assembly — collapsed, clearly labeled */}
        <div className="mt-8 border-t border-gray-800 pt-4">
          <button
            onClick={() => setShowAdvanced(s => !s)}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            {showAdvanced ? '▼' : '▶'} Advanced: Manual Bundle Assembly
          </button>
          {showAdvanced && (
            <form onSubmit={handleGenerate} className="bg-gray-800 rounded-xl border border-gray-700 p-5 mt-3">
              <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-300 mb-4">
                This creates an empty bundle shell with no evidence objects attached — it is a
                low-level tool for manually assembling a bundle from hand-picked object refs
                (not exposed in this UI yet), not a way to generate a complete evidence packet.
                Use the Evidence Packet Generator for that.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Packet type</label>
                  <select
                    value={bundleType}
                    onChange={e => setBundleType(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {BUNDLE_TYPES.map(b => (
                      <option key={b.value} value={b.value}>{b.label} — {b.desc}</option>
                    ))}
                  </select>
                </div>
                <div />
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Period start</label>
                  <input
                    type="datetime-local"
                    value={periodStart}
                    onChange={e => setPeriodStart(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Period end</label>
                  <input
                    type="datetime-local"
                    value={periodEnd}
                    onChange={e => setPeriodEnd(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={generating || !siteId}
                className="px-5 py-2 text-sm rounded-lg bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-gray-200 font-medium transition-colors"
              >
                {generating ? 'Generating...' : 'Generate Empty Bundle Shell'}
              </button>
              {!siteId && <p className="text-xs text-gray-500 mt-2">Select a site above first.</p>}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
